import { useEffect, useRef } from 'react';
import {
  MapView,
  type MapDocument,
  type SelectPayload,
  type ViewMode,
} from '../../lib/renderer';

/**
 * React wrapper around the imperative renderer.
 *
 * The renderer owns the canvas and all of its state; this component owns the
 * element and the subscription, and pushes prop changes in through the setters
 * rather than re-creating anything. One effect per setter keeps each update as
 * narrow as the renderer allows — changing the view mode does not rebuild the
 * scene, changing the data does.
 */
export function MapCanvas({
  document: doc,
  mode,
  scoreField,
  maxLines,
  selectedId,
  onSelect,
  theme,
  onReady,
}: {
  document: MapDocument;
  mode: ViewMode;
  scoreField: string | null;
  /** Hard cap on drawn link lines; this is what the density slider controls. */
  maxLines: number;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  theme: 'light' | 'dark';
  /** Handed the live view so the result screen can export a PNG of it. */
  onReady?: (view: MapView | null) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const viewRef = useRef<MapView | null>(null);
  // The renderer emits `select` for its own clicks and for our `select()`
  // calls alike. Remembering the last id it told us about stops the two from
  // bouncing off each other.
  const echoed = useRef<string | null>(null);

  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  // Captured once: the constructor takes the theme, and later changes go
  // through setOptions rather than rebuilding the view.
  const initialTheme = useRef(theme);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const view = new MapView(canvas, {
      theme: initialTheme.current,
      // Pinned, not left on 'auto'. journalmap writes VOSviewer's convention --
      // cluster numbers start at 1 and a missing cluster is null -- and the
      // adapter turns null into 0. 'auto' reads a single 0 as "this file is
      // 0-based" and shifts every colour by one, so a map with one unclustered
      // item would come out miscoloured against its own legend.
      clusterBase: 1,
    });
    viewRef.current = view;
    onReadyRef.current?.(view);

    const off = view.on('select', (payload) => {
      const { item } = payload as SelectPayload;
      const id = item ? item.id : null;
      echoed.current = id;
      onSelectRef.current(id);
    });

    return () => {
      off();
      view.destroy();
      viewRef.current = null;
      onReadyRef.current?.(null);
    };
  }, []);

  useEffect(() => {
    viewRef.current?.setData(doc);
  }, [doc]);

  useEffect(() => {
    viewRef.current?.setMode(mode);
  }, [mode]);

  useEffect(() => {
    viewRef.current?.setScoreField(scoreField);
  }, [scoreField]);

  useEffect(() => {
    // No lower bound on strength: the slider is "how many of the strongest",
    // which is a count, and a strength threshold would behave differently on
    // every analysis.
    viewRef.current?.setLinkVisibility(0, maxLines);
  }, [maxLines]);

  useEffect(() => {
    viewRef.current?.setOptions({ theme });
  }, [theme]);

  useEffect(() => {
    if (selectedId === echoed.current) return;
    echoed.current = selectedId;
    viewRef.current?.select(selectedId);
  }, [selectedId]);

  return <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: '100%' }} />;
}
